import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

const PlayerTable = ({ apiUrl }) => {
    const [allPlayers, setAllPlayers] = useState([]);
    const [filteredPlayers, setFilteredPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // State for filters
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedPosition, setSelectedPosition] = useState('');
    const [selectedLeague, setSelectedLeague] = useState('');
    const [selectedConference, setSelectedConference] = useState('');
    const [selectedDivision, setSelectedDivision] = useState('');
    const [selectedSeason, setSelectedSeason] = useState('');
    const [selectedLastUpdated, setSelectedLastUpdated] = useState('');

    // State for dropdown options
    const [teamOptions, setTeamOptions] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [leagueOptions, setLeagueOptions] = useState([]);
    const [conferenceOptions, setConferenceOptions] = useState([]);
    const [divisionOptions, setDivisionOptions] = useState([]);
    const [seasonOptions, setSeasonOptions] = useState([]);

    const baseballPositionOrder = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'SP', 'RP'];

    useEffect(() => {
        fetch(apiUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                const playersWithIds = data.map((player, index) => ({
                    ...player,
                    id: player.uid || index,
                    // Keep original user object for URL, and pre-process username for display/filtering
                    usernameDisplay: player.user ? player.user.username : 'N/A',
                    team_name: player.team ? player.team.team_name : 'N/A',
                    league_name: player.team && player.team.division && player.team.division.conference && player.team.division.conference.league ? player.team.division.conference.league.league_name : 'N/A',
                    conference_name: player.team && player.team.division && player.team.division.conference ? player.team.division.conference.conference_name : 'N/A',
                    division_name: player.team && player.team.division ? player.team.division.division_name : 'N/A',
                }));
                setAllPlayers(playersWithIds);
                setFilteredPlayers(playersWithIds);

                setTeamOptions([...new Set(playersWithIds.map(p => p.team_name).filter(Boolean))].sort());

                const uniquePositions = [...new Set(playersWithIds.map(p => p.position).filter(Boolean))];
                uniquePositions.sort((a, b) => {
                    const indexA = baseballPositionOrder.indexOf(a);
                    const indexB = baseballPositionOrder.indexOf(b);

                    if (indexA !== -1 && indexB !== -1) { // Both are in custom order
                        return indexA - indexB;
                    }
                    if (indexA !== -1) { // Only A is in custom order, so A comes first
                        return -1;
                    }
                    if (indexB !== -1) { // Only B is in custom order, so B comes first
                        return 1;
                    }
                    return a.localeCompare(b); // Neither in custom order, sort alphabetically
                });
                setPositionOptions(uniquePositions);

                setLeagueOptions([...new Set(playersWithIds.map(p => p.league_name).filter(Boolean))].sort());
                setConferenceOptions([...new Set(playersWithIds.map(p => p.conference_name).filter(Boolean))].sort());
                setDivisionOptions([...new Set(playersWithIds.map(p => p.division_name).filter(Boolean))].sort());
                setSeasonOptions([...new Set(playersWithIds.map(p => p.season).filter(Boolean))].sort((a, b) => b - a));

                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching data:", error);
                setError(error);
                setLoading(false);
            });
    }, [apiUrl]);

    useEffect(() => {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        let tempFilteredPlayers = allPlayers;

        if (lowercasedSearchTerm) {
            tempFilteredPlayers = tempFilteredPlayers.filter(player => {
                const usernameMatch = player.usernameDisplay?.toLowerCase().includes(lowercasedSearchTerm); // Use usernameDisplay
                const playerNameMatch = player.player_name?.toLowerCase().includes(lowercasedSearchTerm);
                return usernameMatch || playerNameMatch;
            });
        }

        if (selectedTeam) {
            tempFilteredPlayers = tempFilteredPlayers.filter(p => p.team_name === selectedTeam);
        }
        if (selectedPosition) {
            tempFilteredPlayers = tempFilteredPlayers.filter(p => p.position === selectedPosition);
        }
        if (selectedLeague) {
            tempFilteredPlayers = tempFilteredPlayers.filter(p => p.league_name === selectedLeague);
        }
        if (selectedConference) {
            tempFilteredPlayers = tempFilteredPlayers.filter(p => p.conference_name === selectedConference);
        }
        if (selectedDivision) {
            tempFilteredPlayers = tempFilteredPlayers.filter(p => p.division_name === selectedDivision);
        }
        if (selectedSeason !== '') {
            tempFilteredPlayers = tempFilteredPlayers.filter(p => p.season === selectedSeason);
        }
        if (selectedLastUpdated) {
            tempFilteredPlayers = tempFilteredPlayers.filter(p => {
                if (!p.last_updated) return false;
                return p.last_updated.startsWith(selectedLastUpdated);
            });
        }
        setFilteredPlayers(tempFilteredPlayers);
    }, [searchTerm, allPlayers, selectedTeam, selectedPosition, selectedLeague, selectedConference, selectedDivision, selectedSeason, selectedLastUpdated]);

    const columns = [
        {
            field: 'player_name',
            headerName: 'Player Name',
            width: 200,
            renderCell: (params) => (
                params.row.url ? (
                    <Link href={params.row.url} target="_blank" rel="noopener">
                        {params.value}
                    </Link>
                ) : params.value
            ),
        },
        {
            field: 'usernameDisplay', // Use the new field for the column
            headerName: 'Username',
            width: 150,
            renderCell: (params) => (
                params.row.user && params.row.user.url ? (
                    <Link href={params.row.user.url} target="_blank" rel="noopener">
                        {params.value}
                    </Link>
                ) : params.value
            ),
        },
        { field: 'tpe', headerName: 'TPE Total', type: 'number', width: 100 },
        { field: 'season', headerName: 'Player Season', type: 'number', width: 130 },
        { field: 'team_name', headerName: 'Team', width: 150 },
        { field: 'position', headerName: 'Position', width: 100 },
        { field: 'league_name', headerName: 'League', width: 120 },
        { field: 'conference_name', headerName: 'Conference', width: 150 },
        { field: 'division_name', headerName: 'Division', width: 120 },
        {
            field: 'last_updated',
            headerName: 'Last Updated',
            width: 180,
            renderCell: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
            },
        },
    ];

    if (loading) {
        return <div>Loading players...</div>;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    const createDropdown = (label, value, onChange, options) => {
        // Using a consistent ID generation for the label
        const labelId = `${label.toLowerCase().replace(/\s+/g, '-')}-select-label`;

        return (
            <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth variant="outlined">
                    {/* Add shrink={true} to the InputLabel */}
                    <InputLabel id={labelId} shrink={true}>
                        {label}
                    </InputLabel>
                    <Select
                        labelId={labelId} // Associates Select with InputLabel
                        displayEmpty
                        value={value}
                        onChange={onChange}
                        label={label} // Still needed for the outlined variant's notch
                        sx={{ width: '100%' }}
                    >
                        <MenuItem value="">
                            <em>All {label}s</em>
                        </MenuItem>
                        {options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
        );
    };

    return (
        <div>
            <Box sx={{ mb: 2, p: 2, border: '1px solid lightgray', borderRadius: '4px' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} lg={selectedLastUpdated ? 6 : 12}>
                        <TextField
                            label="Player name or username"
                            variant="outlined"
                            fullWidth
                            // size="small" REMOVE THIS LINE
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Grid>
                    {createDropdown("Team", selectedTeam, (e) => setSelectedTeam(e.target.value), teamOptions)}
                    {createDropdown("Position", selectedPosition, (e) => setSelectedPosition(e.target.value), positionOptions)}
                    {createDropdown("League", selectedLeague, (e) => setSelectedLeague(e.target.value), leagueOptions)}
                    {createDropdown("Conference", selectedConference, (e) => setSelectedConference(e.target.value), conferenceOptions)}
                    {createDropdown("Division", selectedDivision, (e) => setSelectedDivision(e.target.value), divisionOptions)}
                    {createDropdown("Season", selectedSeason, (e) => setSelectedSeason(e.target.value), seasonOptions)}
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                        <TextField
                            label="Last Updated Date"
                            type="date"
                            variant="outlined"
                            fullWidth
                            value={selectedLastUpdated}
                            onChange={(e) => setSelectedLastUpdated(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <div style={{ height: 700, width: '100%' }}>
                <DataGrid
                    rows={filteredPlayers}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectionOnClick
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'tpe', sort: 'desc' }],
                        },
                    }}
                />
            </div>
        </div>
    );
};

export default PlayerTable;